import IconExcel from "@/assets/icones/excel.svg?react";
import IconImg from "@/assets/icones/images.svg?react";
import IconPdf from "@/assets/icones/pdf.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import SwitchList from "@/components/SwitchList";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import AddDocuments from "@/documents/components/AddDocuments";
import DocumentFolderExplorer from "@/documents/components/DocumentFolderExplorer";
import FilterDocument from "@/documents/components/FilterDocument";
import ImportDocumentFolderButton from "@/documents/components/ImportDocumentFolderButton";
import { usePersistDocumentRoute } from "@/documents/hooks/usePersistDocumentRoute";
import {
  buildDocumentBreadcrumb,
  DocumentFolderTreeResponse,
  normalizeDocumentTypes,
} from "@/documents/utils/documentTree";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { Document, ProgramType, TreeRoot } from "@/types";
import { UploadCloud } from "lucide-react";
import type { DragEvent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/hooks/useAuth";
import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { useSearchParams } from "react-router-dom";
/* eslint-disable react/react-in-jsx-scope */

const DOCUMENT_VIEW_STORAGE_KEY = "documents-view-preference";
const DOCUMENTS_BROWSER_CACHE_TTL = 1000 * 60 * 60;
const EMBEDDED_PROJECT_DOCUMENT_INIT_KEY = "embedded-project-document-init";
const EMBEDDED_DOCUMENT_FILTER_KEYS = [
  "scope",
  "origin",
  "intervenants",
  "subcontractors",
  "categories",
  "types",
  "folderIds",
  "search",
  "date",
  "after",
  "before",
] as const;

type DocumentsProps = {
  embeddedInProject?: boolean;
  projectId?: number | string | null;
  programId?: number | string | null;
};

type ProjectFolderNode = {
  id?: number | null;
  name?: string | null;
  defaultKey?: string | null;
  documentType?: number | null;
  documentTypes?: number[];
  children?: ProjectFolderNode[];
};

const normalizePositiveId = (value?: number | string | null) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : null;
};

const PROJECT_CONTRACTUAL_DOC_TYPES = [25, 26, 27];
const PROJECT_REFERENCE_DOC_TYPES = [2, 3, 7, 8];
const PROJECT_REPORT_DOC_TYPES = [10, 11, 12, 13, 14, 15];

const hasSameIds = (left: number[] = [], right: number[] = []) => {
  const normalizedLeft = [...new Set(left)].sort((a, b) => a - b);
  const normalizedRight = [...new Set(right)].sort((a, b) => a - b);

  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((value, index) => value === normalizedRight[index])
  );
};

const getProjectFolderTypeIds = (folder: ProjectFolderNode): number[] => {
  const defaultKey = String(folder.defaultKey ?? "");

  if (defaultKey.startsWith("type-")) {
    const typeId = Number(defaultKey.replace("type-", ""));
    return Number.isFinite(typeId) && typeId > 0 ? [typeId] : [];
  }

  if (defaultKey === "contractuel") {
    return PROJECT_CONTRACTUAL_DOC_TYPES;
  }

  if (defaultKey === "rapport") {
    return PROJECT_REPORT_DOC_TYPES;
  }

  const documentTypes = Array.isArray(folder.documentTypes)
    ? folder.documentTypes
        .map(Number)
        .filter((value) => Number.isFinite(value) && value > 0)
    : [];

  if (documentTypes.length > 0) {
    return documentTypes;
  }

  const documentType = Number(folder.documentType ?? 0);
  return Number.isFinite(documentType) && documentType > 0 ? [documentType] : [];
};

const findProjectFolderPath = (
  folders: ProjectFolderNode[] = [],
  folderId: number,
): ProjectFolderNode[] | null => {
  for (const folder of folders) {
    if (Number(folder?.id ?? 0) === folderId) {
      return [folder];
    }

    const nested = findProjectFolderPath(folder.children ?? [], folderId);
    if (nested) {
      return [folder, ...nested];
    }
  }

  return null;
};

const findProjectDefaultFolder = (
  folders: ProjectFolderNode[] = [],
  matcher: (folder: ProjectFolderNode) => boolean,
): ProjectFolderNode | null => {
  for (const folder of folders) {
    if (matcher(folder)) {
      return folder;
    }

    const nested = findProjectDefaultFolder(folder.children ?? [], matcher);
    if (nested) {
      return nested;
    }
  }

  return null;
};

const getDocumentCategoryLabel = (typesDocs: any[], categoryId: number) => {
  for (const group of typesDocs ?? []) {
    if (Number(group?.value) === categoryId) {
      return group.label;
    }

    const matchingChild = (group?.children ?? []).find(
      (item: any) => Number(item?.value) === categoryId,
    );

    if (matchingChild?.label) {
      return matchingChild.label;
    }
  }

  return "Documents";
};

const Documents = ({
  embeddedInProject = false,
  projectId,
  programId,
}: DocumentsProps = {}) => {
  const isRestoringDocumentRoute = usePersistDocumentRoute();
  const [searchParams, setSearchParams] = useSearchParams();
  const forcedProjectId = normalizePositiveId(projectId);
  const forcedProgramId = normalizePositiveId(programId);
  const lockedSearchParams = useMemo(
    () => ({
      ...(forcedProgramId ? { programs: forcedProgramId } : {}),
      ...(forcedProjectId ? { projects: forcedProjectId } : {}),
    }),
    [forcedProgramId, forcedProjectId],
  );
  const effectiveSearchParams = useMemo(() => {
    const params = new URLSearchParams(searchParams.toString());

    Object.entries(lockedSearchParams).forEach(([key, value]) => {
      params.set(key, String(value));
    });

    return params;
  }, [lockedSearchParams, searchParams]);
  const searchParamsKey = effectiveSearchParams.toString();
  const { t } = useTranslation("dashboard", {
    keyPrefix: "details",
  });
  const [view, setView] = useState<"list" | "card">(() => {
    const storedView = localStorage.getItem(DOCUMENT_VIEW_STORAGE_KEY);

    if (storedView === "list" || storedView === "card") {
      return storedView;
    }

    return "list";
  });
   const [programsList, setProgramsList] = useState<Array<{ id: number; name: string; projects: number[] }>>([]);
   const [projectList, setProjectList] = useState<Array<{ id: number; name: string }>>([]);
  const [typesDocs, setTypesDocs] = useState<any[]>([]);
  const {user} = useAuth() ; 
  const hidePostActions = useHidePostActions();
  const dragDepthRef = useRef(0);
  const [isPageDragActive, setIsPageDragActive] = useState(false);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [droppedFolderImport, setDroppedFolderImport] = useState<{
    name: string;
    files: File[];
    directoryPaths?: string[];
  } | null>(null);
  const hasDraggedFiles = (event: DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types).includes("Files");

  const isDocumentsRootView =
    !(effectiveSearchParams.get("scope") ?? "") &&
    !(effectiveSearchParams.get("programs") ?? "") &&
    !(effectiveSearchParams.get("projects") ?? "") &&
    !(effectiveSearchParams.get("folderIds") ?? "") &&
    !(effectiveSearchParams.get("intervenants") ?? "") &&
    !(effectiveSearchParams.get("origin") ?? "");
  const { data: dataDocuments, loading: loadingDocuments } = useFetchDataV2<
    Document[]
  >(
    `${import.meta.env.VITE_API_ENDPOINT}/docs/carrier`,
    {
      browserCache: true,
      browserCacheKey: "docs-carrier",
      browserCacheNamespace: "documents",
      browserCacheTtlMs: DOCUMENTS_BROWSER_CACHE_TTL,
      cacheId: "docs-carrier",
      staleTime: DOCUMENTS_BROWSER_CACHE_TTL,
    },
  );
    const {
      data: treeFinancer,
    } = useFetchDataV2<TreeRoot[]>(
      `${import.meta.env.VITE_API_ENDPOINT}/tree/carrier`,
      {
        browserCache: true,
        browserCacheKey: "tree-financer",
        browserCacheNamespace: "documents",
        browserCacheTtlMs: DOCUMENTS_BROWSER_CACHE_TTL,
        cacheId: "tree-financer",
        staleTime: DOCUMENTS_BROWSER_CACHE_TTL,
      },
    );
  const { data: programsInfo } = useFetchDataV2<ProgramType[]>(
    `${import.meta.env.VITE_API_ENDPOINT}/info/programs`,
    {
      browserCache: true,
      browserCacheKey: "programs-info",
      browserCacheNamespace: "documents",
      browserCacheTtlMs: DOCUMENTS_BROWSER_CACHE_TTL,
      staleTime: DOCUMENTS_BROWSER_CACHE_TTL,
    },
  );
  const { data: subcontractorsData } = useFetchDataV2<any>(
    `${import.meta.env.VITE_API_ENDPOINT}/users/subcontractors`,
    {
      browserCache: true,
      browserCacheKey: "subcontractors",
      browserCacheNamespace: "documents",
      browserCacheTtlMs: DOCUMENTS_BROWSER_CACHE_TTL,
      staleTime: DOCUMENTS_BROWSER_CACHE_TTL,
    },
  );
  const { data: customFolders } = useFetchDataV2<DocumentFolderTreeResponse>(
    `${import.meta.env.VITE_API_ENDPOINT}/document-folders/tree`,
    {
      browserCache: true,
      browserCacheKey: "document-folders-tree",
      browserCacheNamespace: "documents",
      browserCacheTtlMs: DOCUMENTS_BROWSER_CACHE_TTL,
      staleTime: DOCUMENTS_BROWSER_CACHE_TTL,
    },
  );
  const [currentDocuments, setCurrentDocuments] = useState<any>(null);
  const [dataFiltered, setDataFiltered] = useState<Document[]>([]);
  const carrierOwnerId = useMemo(
    () => Number(user?.id ?? 0) || null,
    [user],
  );
  const canUploadDocuments = useMemo(
    () =>
      Boolean(
        user &&
          !hidePostActions &&
          (user?.roles?.includes("ROLE_CARRIER") ||
            user?.roles?.includes("ROLE_CPINTERVENANT") ||
            (user?.roles?.includes("ROLE_SUBCONTRACTOR") && user?.fullAccess)),
      ),
    [hidePostActions, user],
  );
  const canDragDropDocuments = canUploadDocuments;

  useEffect(() => {
    if (!embeddedInProject || Object.keys(lockedSearchParams).length === 0) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams.toString());
    let hasChanged = false;

    Object.entries(lockedSearchParams).forEach(([key, value]) => {
      const nextValue = String(value);
      if (nextParams.get(key) !== nextValue) {
        nextParams.set(key, nextValue);
        hasChanged = true;
      }
    });

    if (hasChanged) {
      setSearchParams(nextParams, {
        replace: true,
        preventScrollReset: true,
      });
    }
  }, [embeddedInProject, lockedSearchParams, searchParams, setSearchParams]);

  useEffect(() => {
    if (!embeddedInProject || !forcedProjectId) {
      return;
    }

    const initializationKey = `${EMBEDDED_PROJECT_DOCUMENT_INIT_KEY}:${forcedProjectId}`;
    if (sessionStorage.getItem(initializationKey) === "1") {
      return;
    }

    const nextParams = new URLSearchParams(searchParams.toString());
    let hasChanged = false;

    EMBEDDED_DOCUMENT_FILTER_KEYS.forEach((key) => {
      if (nextParams.has(key)) {
        nextParams.delete(key);
        hasChanged = true;
      }
    });

    Object.entries(lockedSearchParams).forEach(([key, value]) => {
      const nextValue = value == null ? "" : String(value);
      if (nextValue && nextParams.get(key) !== nextValue) {
        nextParams.set(key, nextValue);
        hasChanged = true;
      }
    });

    sessionStorage.setItem(initializationKey, "1");

    if (hasChanged) {
      setSearchParams(nextParams, {
        replace: true,
        preventScrollReset: true,
      });
    }
  }, [
    embeddedInProject,
    forcedProjectId,
    lockedSearchParams,
    searchParams,
    setSearchParams,
  ]);

  const enrichedTreeFinancer = useMemo(() => {
    if (!treeFinancer) {
      return [];
    }

    const programsInfoById = new Map(
      (programsInfo ?? []).map((program) => [Number(program.id), program]),
    );
    const projectsInfoById = new Map<number, { id: number; name: string }>();
    const subcontractorsById = new Map<number, {
      id: number;
      firstname?: string | null;
      lastname?: string | null;
      fullname?: string | null;
      email?: string | null;
      type?: "subcontractor";
    }>();

    (programsInfo ?? []).forEach((program) => {
      (program.projects ?? []).forEach((project) => {
        projectsInfoById.set(Number(project.id), {
          id: Number(project.id),
          name: project.name,
        });
      });
    });

    (Array.isArray(subcontractorsData?.subcontractors) ? subcontractorsData.subcontractors : [])
      .forEach((item: any) => {
        const subcontractorId = Number(item?.id ?? 0);
        if (!Number.isFinite(subcontractorId) || subcontractorId <= 0) {
          return;
        }

        const organisationName = String(item?.Organisation?.name || item?.organisation?.name || "").trim();
        const fullName =
          organisationName ||
          [item?.lastname, item?.firstname]
            .map((value) => String(value ?? "").trim())
            .filter(Boolean)
            .join(" ")
            .trim();

        if (!fullName || /^Intervenant( #\d+)?$/i.test(fullName)) {
          return;
        }

        subcontractorsById.set(subcontractorId, {
          id: subcontractorId,
          firstname: item?.firstname ?? null,
          lastname: item?.lastname ?? null,
          fullname: fullName,
          email: item?.email ?? null,
          type: "subcontractor",
        });
      });

    return treeFinancer.map((carrier) => ({
      ...carrier,
      intervenants: [
        ...(carrier.intervenants ?? []).map((intervenant) => {
          const subcontractor = subcontractorsById.get(Number(intervenant.id));
          if (!subcontractor) {
            return intervenant;
          }

          const currentName = String(intervenant.fullname || "").trim();
          if (currentName && !/^Intervenant( #\d+)?$/i.test(currentName)) {
            return intervenant;
          }

          return {
            ...intervenant,
            ...subcontractor,
          };
        }),
        ...Array.from(subcontractorsById.values()).filter(
          (subcontractor) =>
            !(carrier.intervenants ?? []).some(
              (intervenant) => Number(intervenant.id) === Number(subcontractor.id),
            ),
        ),
      ],
      programs: (carrier.programs ?? []).map((program) => {
        const programInfo = programsInfoById.get(Number(program.id));

        return {
          ...program,
          name: programInfo?.name || program.name,
          projects: (program.projects ?? []).map((project) => {
            const projectInfo = projectsInfoById.get(Number(project.id));

            return {
              ...project,
              name: projectInfo?.name || project.name,
            };
          }),
        };
      }),
    }));
  }, [programsInfo, subcontractorsData, treeFinancer]);

  const readDirectoryEntries = async (reader: any): Promise<any[]> => {
    const entries: any[] = [];

    while (true) {
      const batch = await new Promise<any[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });

      if (batch.length === 0) {
        return entries;
      }

      entries.push(...batch);
    }
  };

  const collectEntryImport = async (
    entry: any,
    currentPath = "",
  ): Promise<{ files: File[]; directoryPaths: string[] }> => {
    if (entry?.isFile) {
      return new Promise<{ files: File[]; directoryPaths: string[] }>((resolve, reject) => {
        entry.file((file: File) => {
          const clonedFile = new File([file], file.name, {
            type: file.type,
            lastModified: file.lastModified,
          });

          Object.defineProperty(clonedFile, "webkitRelativePath", {
            value: `${currentPath}${file.name}`,
          });
          resolve({ files: [clonedFile], directoryPaths: [] });
        }, reject);
      });
    }

    if (entry?.isDirectory) {
      const entries = await readDirectoryEntries(entry.createReader());
      const directoryPath = `${currentPath}${entry.name}`;
      const nestedImports = await Promise.all(
        entries.map((nestedEntry) => collectEntryImport(nestedEntry, `${directoryPath}/`)),
      );
      return {
        files: nestedImports.flatMap((item) => item.files),
        directoryPaths: [
          directoryPath,
          ...nestedImports.flatMap((item) => item.directoryPaths),
        ],
      };
    }

    return { files: [], directoryPaths: [] };
  };

  const getDroppedFolderImport = async (dataTransfer: DataTransfer) => {
    const directoryEntries = Array.from(dataTransfer.items ?? [])
      .map((item: any) => item.webkitGetAsEntry?.())
      .filter((entry: any) => entry?.isDirectory);

    if (directoryEntries.length > 0) {
      const entry = directoryEntries[0];
      const imported = await collectEntryImport(entry);
      return {
        name: entry.name || "Dossier importé",
        files: imported.files,
        directoryPaths: imported.directoryPaths,
      };
    }

    const files = Array.from(dataTransfer.files ?? []);
    const relativeFolder = (files[0] as File & { webkitRelativePath?: string } | undefined)?.webkitRelativePath
      ?.split("/")
      .filter(Boolean)[0];

    if (relativeFolder) {
      return { name: relativeFolder, files };
    }

    return null;
  };
  const breadcrumbItems = useMemo(
    () => {
      const dynamicItems = buildDocumentBreadcrumb({
        carrierTree: enrichedTreeFinancer,
        customFolders,
        searchParams: new URLSearchParams(searchParamsKey),
        currentUserId: carrierOwnerId,
        documentsData: currentDocuments,
      });

      return [
        {
          href: dynamicItems.length > 0 ? `/dashboard/mes-documents` : undefined,
          name: t("Mes documents"),
        },
        ...dynamicItems,
      ];
    },
    [carrierOwnerId, currentDocuments, customFolders, enrichedTreeFinancer, searchParamsKey, t],
  );
  const embeddedBreadcrumbItems = useMemo(
    () => {
      if (!embeddedInProject || !forcedProjectId) {
        return breadcrumbItems.filter(
          (item) => item.name && item.name !== t("Mes documents"),
        );
      }

      const projectFolders = (
        customFolders?.project?.[String(forcedProjectId)] ?? []
      ) as ProjectFolderNode[];
      const currentFolderId = Number(
        effectiveSearchParams.get("folderIds")?.split(",")[0] ?? 0,
      );
      const currentCategoryIds = (effectiveSearchParams.get("categories") ?? "")
        .split(",")
        .map(Number)
        .filter((value) => Number.isFinite(value) && value > 0);
      const currentTypes = (effectiveSearchParams.get("types") ?? "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);
      const currentProgramId =
        (
          forcedProgramId ??
          Number(effectiveSearchParams.get("programs")?.split(",")[0] ?? 0)
        ) || null;
      const projectBreadcrumbItem =
        breadcrumbItems.find((item) => {
          if (!item.href) {
            return false;
          }

          const href = new URL(item.href, window.location.origin);
          const projectParam = href.searchParams.get("projects")?.split(",")[0] ?? "";
          return Number(projectParam) === forcedProjectId;
        }) ?? null;
      const projectRootItem = projectBreadcrumbItem
        ? {
            name: projectBreadcrumbItem.name,
            href: projectBreadcrumbItem.href,
          }
        : {
            name:
              projectList.find((item) => Number(item.id) === forcedProjectId)?.name ||
              `Projet ${forcedProjectId}`,
            href: `/dashboard/mes-documents?${
              new URLSearchParams({
                ...(currentProgramId ? { programs: String(currentProgramId) } : {}),
                projects: String(forcedProjectId),
              }).toString()
            }`,
          };

      const buildProjectFolderHref = (folder: ProjectFolderNode) => {
        const params = new URLSearchParams();
        if (currentProgramId) {
          params.set("programs", String(currentProgramId));
        }
        params.set("projects", String(forcedProjectId));

        const folderTypeIds = getProjectFolderTypeIds(folder);
        if (folderTypeIds.length > 0) {
          params.set("categories", folderTypeIds.join(","));
        }

        if (Number(folder?.id ?? 0) > 0) {
          params.set("folderIds", String(folder.id));
        }

        return `/dashboard/mes-documents?${params.toString()}`;
      };

      if (currentFolderId > 0) {
        const folderPath = findProjectFolderPath(projectFolders, currentFolderId) ?? [];
        const visibleFolderPath = folderPath.filter(
          (folder, index) => !(index === 0 && String(folder.defaultKey ?? "") === "root"),
        );

        return [
          projectRootItem,
          ...visibleFolderPath.map((folder, index) => ({
            name: folder.name?.trim() || "Nouveau dossier",
            href:
              index === visibleFolderPath.length - 1
                ? undefined
                : buildProjectFolderHref(folder),
          })),
        ];
      }

      if (currentTypes.includes("image")) {
        const photoFolder = findProjectDefaultFolder(
          projectFolders,
          (folder) => String(folder.defaultKey ?? "") === "photos",
        );

        return [
          projectRootItem,
          { name: photoFolder?.name?.trim() || "Photos" },
        ];
      }

      if (currentCategoryIds.length > 0) {
        const isReferenceRootCategory =
          currentCategoryIds.length === 1 && currentCategoryIds[0] === 100;
        const defaultFolder = findProjectDefaultFolder(projectFolders, (folder) => {
          const defaultKey = String(folder.defaultKey ?? "");

          if (
            isReferenceRootCategory &&
            (defaultKey === "root" ||
              hasSameIds(
                getProjectFolderTypeIds(folder),
                PROJECT_REFERENCE_DOC_TYPES,
              ))
          ) {
            return true;
          }

          if (currentCategoryIds.length === 1 && defaultKey === `type-${currentCategoryIds[0]}`) {
            return true;
          }

          return hasSameIds(getProjectFolderTypeIds(folder), currentCategoryIds);
        });

        const fallbackCategoryLabel = getDocumentCategoryLabel(
          typesDocs,
          currentCategoryIds[0] ?? 0,
        );

        return [
          projectRootItem,
          { name: defaultFolder?.name?.trim() || fallbackCategoryLabel },
        ];
      }

      return [projectRootItem];
    },
    [
      breadcrumbItems,
      customFolders,
      effectiveSearchParams,
      embeddedInProject,
      forcedProgramId,
      forcedProjectId,
      projectList,
      t,
      typesDocs,
    ],
  );
  const handleEmbeddedBreadcrumbClick = (href?: string) => {
    if (!embeddedInProject || !href) {
      return;
    }

    const targetUrl = new URL(href, window.location.origin);
    const nextParams = new URLSearchParams(targetUrl.search);

    Object.entries(lockedSearchParams).forEach(([key, value]) => {
      nextParams.set(key, String(value));
    });

    const currentTab = searchParams.get("tab");
    const currentStep = searchParams.get("step");

    if (currentTab && !nextParams.get("tab")) {
      nextParams.set("tab", currentTab);
    }

    if (currentStep && !nextParams.get("step")) {
      nextParams.set("step", currentStep);
    }

    setSearchParams(nextParams, {
      replace: false,
      preventScrollReset: true,
    });
  };

  const handleDeleteDocument = (id: number) => {
    setCurrentDocuments((prev: any) => {
      if (!prev) return prev;

      return Object.fromEntries(
        Object.entries(prev).map(([key, value]) => [
          key,
          Array.isArray(value)
            ? value.filter((doc: any) => Number(doc.id) !== Number(id))
            : value,
        ]),
      );
    });
  };

  useEffect(() => {
    setCurrentDocuments(dataDocuments ?? null);
  }, [dataDocuments]);

  useEffect(() => {
    localStorage.setItem(DOCUMENT_VIEW_STORAGE_KEY, view);
  }, [view]);

  useEffect(() => {
    const localStorageKey = "typesDocsCacheV3";

    const cached = localStorage.getItem(localStorageKey);
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          setTypesDocs(normalizeDocumentTypes(parsed));
        }
      } catch (e) {
        console.warn(
          "Échec du parsing du cache localStorage, on refait un fetch"
        );
      }
    }
    // On fetch quand même pour vérifier si c’est à jour
    fetch(`${import.meta.env.VITE_IP_API}/types-docs.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (data) {
          setTypesDocs(() => {
            // On compare avec la version actuelle du localStorage
            const current = localStorage.getItem(localStorageKey);
            let isSame = false;

            if (current) {
              try {
                const parsed = JSON.parse(current);
                // 🔍 Compare les données (ici stringify pour simplicité)
                isSame = JSON.stringify(parsed) === JSON.stringify(data);
               
              } catch {}
            }

            if (!isSame) {
              localStorage.setItem(localStorageKey, JSON.stringify(data));
            }

            return normalizeDocumentTypes(data) as any[];
          });
        }
      })
      .catch((error) => {
        console.error("Erreur lors du fetch :", error);
      });
  }, []);
useEffect(() => {
  if (!enrichedTreeFinancer || enrichedTreeFinancer.length === 0) {
    setProgramsList([]);
    setProjectList([]);
    return;
  }

  const programsById = new Map<number, { id: number; name: string; projects: number[] }>();
  const projectsById = new Map<number, { id: number; name: string }>();

  enrichedTreeFinancer.forEach((carrier) => {
    carrier.programs?.forEach((program) => {
      const existingProgram = programsById.get(program.id);
      const projectIds = (program.projects ?? []).map((project) => project.id);

      programsById.set(program.id, {
        id: program.id,
        name: program.name,
        projects: existingProgram
          ? [...new Set([...existingProgram.projects, ...projectIds])]
          : projectIds,
      });

      (program.projects ?? []).forEach((project) => {
        projectsById.set(project.id, { id: project.id, name: project.name });
      });
    });
  });

  setProgramsList(Array.from(programsById.values()));
  setProjectList(Array.from(projectsById.values()));
}, [enrichedTreeFinancer]);
  const optionsType = useMemo(
    () => [
      {
        value: "document",
        label: "Documents",
        icon: <IconWord width={18} height={18} />,
      },
      {
        value: "spreadsheet",
        label: "Feuilles de calcul",
        icon: <IconExcel width={18} height={18} />,
      },
      {
        value: "image",
        label: "Photos et images",
        icon: <IconImg width={18} height={18} />,
      },
      {
        value: "pdf",
        label: "Fichiers PDF",
        icon: <IconPdf width={18} height={18} />,
      },
    ],
    [],
  );

  if (isRestoringDocumentRoute) {
    return <LoadingSpinner />;
  }

  return (
    <div
      className="relative min-h-[60vh]"
      onDragEnter={(event) => {
        if (!canDragDropDocuments || !hasDraggedFiles(event)) {
          return;
        }

        event.preventDefault();
        dragDepthRef.current += 1;
        setIsPageDragActive(true);
      }}
      onDragOver={(event) => {
        if (!canDragDropDocuments || !hasDraggedFiles(event)) {
          return;
        }

        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={(event) => {
        if (!canDragDropDocuments || !hasDraggedFiles(event)) {
          return;
        }

        event.preventDefault();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) {
          setIsPageDragActive(false);
        }
      }}
      onDrop={async (event) => {
        if (
          !canDragDropDocuments ||
          (!event.dataTransfer.files?.length && !event.dataTransfer.items?.length)
        ) {
          return;
        }

        event.preventDefault();
        dragDepthRef.current = 0;
        setIsPageDragActive(false);
        const folderImport = await getDroppedFolderImport(event.dataTransfer);
        if (folderImport) {
          setDroppedFolderImport(folderImport);
          return;
        }

        setDroppedFiles(Array.from(event.dataTransfer.files));
      }}
    >
      {canUploadDocuments && (
        <>
          <AddDocuments
            externalFiles={droppedFiles}
            hideTrigger
            onExternalFilesConsumed={() => setDroppedFiles([])}
            showProjectSelection
          />
          <ImportDocumentFolderButton
            externalImport={droppedFolderImport}
            hideTrigger
            onExternalImportConsumed={() => setDroppedFolderImport(null)}
            ownerId={carrierOwnerId}
          />
        </>
      )}
      {isPageDragActive && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex w-[min(420px,calc(100vw-32px))] flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed border-[#1d5215] bg-white p-8 text-center shadow-lg">
            <UploadCloud className="h-10 w-10 text-[#1d5215]" />
            <div>
              <p className="text-base font-semibold text-[#1d5215]">
                Déposez vos documents ou dossiers
              </p>
              <p className="mt-1 text-sm text-gray-600">
                Les fichiers ouvrent le formulaire d’ajout. Les dossiers sont importés directement dans le niveau courant.
              </p>
            </div>
          </div>
        </div>
      )}
      {!embeddedInProject && (
        <>
          <div className="mb-5">
            <ReactBreadcrumb
              items={breadcrumbItems}
              isTruncated={false}
            />
          </div>
          <h1 className="text-3xl font-bold mb-5">{t("Mes documents")}</h1>
        </>
      )}
      {embeddedInProject && embeddedBreadcrumbItems.length > 0 && (
        <div className="mb-3 rounded-xl border border-[#e6ecd9] bg-[#f8fbf4] px-4 py-3">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-[#54724b]">
            {embeddedBreadcrumbItems.map((item, index) => {
              const isLastItem = index === embeddedBreadcrumbItems.length - 1;

              return (
                <div key={`${item.name}-${index}`} className="flex items-center gap-2">
                  {item.href && !isLastItem ? (
                    <a
                      href={item.href}
                      onClick={(event) => {
                        event.preventDefault();
                        handleEmbeddedBreadcrumbClick(item.href);
                      }}
                      className="transition-colors hover:text-[#1d5215] hover:underline"
                    >
                      {item.name}
                    </a>
                  ) : (
                    <span className={isLastItem ? "font-medium text-[#1d5215]" : undefined}>
                      {item.name}
                    </span>
                  )}
                  {!isLastItem && <span className="text-[#8aa17f]">/</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className={embeddedInProject ? "pt-4" : undefined}>
        {loadingDocuments && !typesDocs ? (
          <LoadingSpinner />
        ) : (
          <FilterDocument
            typesDocs={typesDocs}
            optionsType={optionsType}
            dataDocuments={currentDocuments}
            setDataFiltered={setDataFiltered}
            programsList={programsList}
            projectList={projectList}
            hideFilters={false}
            hideProjectProgramCategoryFilters
            requireChildFolderForAddFolder={false}
            ownerId={carrierOwnerId}
            customFolders={customFolders}
            searchParamsOverride={effectiveSearchParams}
            lockedSearchParams={lockedSearchParams}
          />
        )}
      </div>
      <DocumentFolderExplorer
        data={dataFiltered}
        carrierTree={enrichedTreeFinancer}
        customFolders={customFolders}
        currentUserId={carrierOwnerId}
        searchParams={new URLSearchParams(searchParamsKey)}
        breadcrumbItems={breadcrumbItems}
        onDelete={handleDeleteDocument}
        filesView={view}
        typesDocs={typesDocs}
        filesToolbar={<SwitchList setView={setView} view={view} showFolderView={false} />}
        stayOnCurrentPath={embeddedInProject}
        lockedSearchParams={lockedSearchParams}
      />
    </div>
  );
};

export default Documents;
